import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/authAPI';
import { formatAPIError } from '../utils/validation';
import PasswordInput from '../components/common/PasswordInput';
import './styles/Login.css';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth();
  const [isEmailLogin, setIsEmailLogin] = useState(true);
  const [credentials, setCredentials] = useState({
    email: '',
    phone: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const isLoginRoute = location.pathname === "/login";

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Handle messages from other pages
  useEffect(() => {
    if (location.state?.message) {
      if (location.state.type === 'error') {
        setError(location.state.message);
      } else {
        setSuccess(location.state.message);
      }
      // Clear the state to prevent message from persisting
      navigate('/login', { replace: true, state: {} });
    }
  }, [location, navigate]);

  const validateForm = () => {
    const newErrors = {};

    if (isEmailLogin) {
      if (!credentials.email) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(credentials.email)) {
        newErrors.email = 'Please enter a valid email';
      }
    } else {
      if (!credentials.phone) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^\+?[1-9]\d{1,14}$/.test(credentials.phone)) {
        newErrors.phone = 'Please enter a valid phone number';
      }
    }

    if (!credentials.password) {
      newErrors.password = 'Password is required';
    }

    return newErrors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear specific field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (loading) return;

  const newErrors = validateForm();
  if (Object.keys(newErrors).length > 0) {
    setErrors(newErrors);
    return;
  }

  setLoading(true);
  setError('');
  setSuccess('');

  try {
    const loginData = isEmailLogin
      ? { email: credentials.email, password: credentials.password }
      : { phone: credentials.phone, password: credentials.password };

    console.log('🔑 Attempting login:', { ...loginData, password: '***' });

    const response = await authAPI.login(loginData);
    
    console.log('🔍 FULL LOGIN RESPONSE:', JSON.stringify(response.data, null, 2));

    //ENHANCED MFA DETECTION LOGIC
    const responseMessage = response.data?.message || '';
    const isMFARequired = responseMessage.toLowerCase().includes('mfa') || 
                          responseMessage.toLowerCase().includes('otp sent');

    if (isMFARequired) {
      console.log('🔐 MFA required detected, redirecting to verification...');
      console.log('- Response message:', responseMessage);
      
      // Extract user with role from response
      const backendUser = response.data.user || response.data.userData || response.data;
      
      console.log('👤 Backend user data:', backendUser);
      
      // Ensure role exists - try multiple locations
      const userRole = backendUser.role || 
                       response.data.role || 
                       backendUser.accountDetails?.role ||
                       response.data.accountDetails?.role;

      console.log('🎭 Extracted role:', userRole);

      // Create complete userData with role
      const completeUserData = {
        id: backendUser.id,
        email: backendUser.email || credentials.email,
        phone: backendUser.phone || credentials.phone,
        role: userRole,           
        firstName: backendUser.firstName,
        lastName: backendUser.lastName,
        mfaEnabled: backendUser.mfaEnabled,
        mfaType: backendUser.mfaType
      };

      console.log('🔑 Complete user data being passed to MFA page:', completeUserData);
      console.log('🎭 Role being passed:', completeUserData.role);

      // Validate role exists before navigating
      if (!completeUserData.role) {
        console.error('⚠️ WARNING: No role found in login response!');
        console.error('📦 Full response.data:', response.data);
        console.error('📦 Backend user:', backendUser);
      }

      navigate('/mfa-verification', {
        state: {
          userData: completeUserData,  // Pass complete data with role
          mfaType: isEmailLogin ? 'email' : 'phone',
          identifier: credentials.email || credentials.phone,
          password: credentials.password // Store for resend functionality
        }
      });
      
      return; // Exit early for MFA flow
    }

    //SUCCESS - NON-MFA LOGIN
    if (response.data.user && response.data.token) {
      setError('');
      setSuccess('🎉 Login successful! Redirecting...');
      
      login(response.data.user, response.data.token);
      
      setTimeout(() => navigate('/dashboard'), 1000);
    } else {
      throw new Error('Invalid response format');
    }

  } catch (error) {
    console.error('❌ Login failed:', error);
    console.error('❌ Full error response:', error.response?.data);

    const errorMessage = String(formatAPIError(error) || 'Login failed');
    console.log('🚨 Error message:', errorMessage);

    if (errorMessage.includes('Account is locked')) {
      setError('🔒 Your account has been locked due to multiple failed login attempts. Please check your email for unlock instructions.');
    } else if (errorMessage.includes('Invalid credentials')) {
      const newFailedAttempts = failedAttempts + 1;
      setFailedAttempts(newFailedAttempts);

      if (newFailedAttempts >= 3) {
        setError('🔒 Account locked due to too many failed attempts. Check your email for unlock instructions.');
      } else {
        const remaining = 3 - newFailedAttempts;
        setError(`❌ Invalid credentials. ${remaining} attempt${remaining !== 1 ? 's' : ''} remaining before account lockout.`);
      }
    } else if (errorMessage.includes("verify")) {
      setError('📧 Please verify your email address before logging in. Check your inbox for verification instructions.');
    } else {
      setError(`❌ Login failed: ${errorMessage}`);
      setFailedAttempts(prev => prev + 1);
    }
  } finally {
    setLoading(false);
  }
};




  const toggleLoginMode = () => {
    setIsEmailLogin(!isEmailLogin);
    setCredentials({
      email: '',
      phone: '',
      password: ''
    });
    setErrors({});
    setError('');
  };

  return (
  <div className="login-page">
    <div className="login-layout">
      {/* LEFT SIDE*/}
      <div className="login-left">
        <div className="login-left-content">
          <h1 className="login-left-title">Sign in to</h1>
          
          <p className="login-left-subtitle">Job Portal</p>

          <div className="login-left-register">
            <p>If you don't have an account registered</p>
            <p>
              You can{" "}
              <Link to="/register" className="login-left-register-link">
                Sign up here !
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE – LOGIN FORM */}
      <div className="login-right">
        <div className="login-form-wrapper">
       {/* Sign In / Join Now (styled like email/phone toggle) */}
<div className="auth-nav">
  <button
    type="button"
    className={`toggle-btn ${isLoginRoute ? "active" : ""}`}
    onClick={() => navigate("/login")}
  >
    Sign In
  </button>

  <button
    type="button"
    className={`toggle-btn ${!isLoginRoute ? "active" : ""}`}
    onClick={() => navigate("/register")}
  >
    Join Now
  </button>
</div>

          <h1 className="login-title">Sign in</h1>
          <div className="form-toggle">
  <button
    type="button"
    className={`toggle-btn ${isEmailLogin ? "active" : ""}`}
    onClick={() => !isEmailLogin && toggleLoginMode()}
  >
    Email
  </button>
  <button
    type="button"
    className={`toggle-btn ${!isEmailLogin ? "active" : ""}`}
    onClick={() => isEmailLogin && toggleLoginMode()}
  >
    Phone
  </button>
</div>


          {/* Messages */}
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              <span className="success-icon">✅</span>
              {success}
            </div>
          )}

          {/* Warning for failed attempts */}
          {failedAttempts > 0 &&
            failedAttempts < 3 &&
            !error.includes("locked") && (
              <div className="warning-message">
                <span className="warning-icon">⚠️</span>
                Warning: {failedAttempts} failed attempt
                {failedAttempts === 1 ? "" : "s"}. Account will be locked after 3
                failed attempts.
              </div>
            )}

          <form onSubmit={handleSubmit} className="login-form">
            {isEmailLogin ? (
              <div className="form-group">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={credentials.email}
                  onChange={handleInputChange}
                  className={`simple-input ${errors.email ? "error" : ""}`}
                  placeholder="Enter email or username"
                  disabled={loading}
                />
                {errors.email && (
                  <span className="error-text">{errors.email}</span>
                )}
              </div>
            ) : (
              <div className="form-group">
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={credentials.phone}
                  onChange={handleInputChange}
                  className={`simple-input ${errors.phone ? "error" : ""}`}
                  placeholder="Enter your phone number"
                  disabled={loading}
                />
                {errors.phone && (
                  <span className="error-text">{errors.phone}</span>
                )}
              </div>
            )}

            <div className="form-group">
              <div className="password-wrapper">
                <PasswordInput
                  type="password"
                  id="password"
                  name="password"
                  value={credentials.password}
                  onChange={handleInputChange}
                  className={`simple-input ${errors.password ? "error" : ""}`}
                  placeholder="Password"
                  disabled={loading}
                />
              </div>
              {errors.password && (
                <span className="error-text">{errors.password}</span>
              )}
            </div>

            <div className="form-actions-simple">
              <Link to="/forgot-password" className="forgot-link">
                Forgot password ?
              </Link>
            </div>

            <button type="submit" disabled={loading} className="simple-login-btn">
              {loading ? (
                <>
                  <span className="loading-spinner"></span>
                  Signing in...
                </>
              ) : (
                "Login"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
);


};

export default Login;
